import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tag, Pencil, Trash2, Plus, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const empty = { name: '', description: '', order: 0 };

export default function AliadoCategoriesPage() {
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/me/oknegocio/product-categories');
            setCats(data);
        } catch { toast.error('Error al cargar categorías'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
    const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, description: c.description || '', order: c.order || 0 }); setShowForm(true); };

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('El nombre es requerido');
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/me/oknegocio/product-categories/${editing}`, form);
                toast.success('Categoría actualizada');
            } else {
                await api.post('/me/oknegocio/product-categories', form);
                toast.success('Categoría creada');
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/me/oknegocio/product-categories/${deleteId}`);
            toast.success('Categoría eliminada');
            setDeleteId(null);
            load();
        } catch { toast.error('Error al eliminar'); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><Tag className="h-5 w-5 text-orange-600" /> Categorías</h1>
                    <p className="text-sm text-muted-foreground">Organiza tu catálogo con categorías propias.</p>
                </div>
                <Button size="sm" onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="h-4 w-4 mr-1" /> Nueva categoría
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground text-center py-8">Cargando…</div>
            ) : cats.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center">
                    <Tag className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">Aún no tienes categorías. Crea la primera para organizar tus productos.</div>
                </div>
            ) : (
                <div className="rounded-2xl border bg-card divide-y">
                    {cats.sort((a, b) => (a.order || 0) - (b.order || 0)).map(c => (
                        <div key={c.id} className="flex items-center gap-3 px-4 py-3">
                            <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">{c.name}</div>
                                {c.description && <div className="text-xs text-muted-foreground truncate">{c.description}</div>}
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="h-7 px-2">
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeleteId(c.id)} className="h-7 px-2 text-destructive hover:bg-destructive/10">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{editing ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium">Nombre *</label>
                            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" placeholder="Ej. Entradas, Bebidas, Electrónica…" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Descripción (opcional)</label>
                            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 text-sm" rows={2} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Orden</label>
                            <Input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="mt-1 w-24" min="0" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                            {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>¿Eliminar categoría?</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Los productos asociados quedarán sin categoría.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
